import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NbAuthResult, NbAuthService, NbTokenStorage } from '@nebular/auth';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { from, Observable } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { UrlHelper } from '../../shared/services/utils/url-helper.service';
import { StrategyNames } from '../auth.module';
import { CurrentMemberProvider } from './current-member-provider.service';

@Injectable()
export class AppAuthService {

  public static readonly loginPage: string = '/auth/login';
  public static readonly defaultPage: string = '/slides/home';

  private authCodeFlowConfig: AuthConfig = {
    issuer: environment.oauthConfig.stsAuthority,
    redirectUri: environment.authorizationRedirectUrl,
    clientId: environment.oauthConfig.clientId,
    responseType: 'code',
    scope: environment.oauthConfig.clientScope,
    logoutUrl: AppAuthService.loginPage,
    showDebugInformation: false,
    requireHttps: environment.envName !== 'dev',
  };

  constructor(
    private oauth: OAuthService,
    private nbAuthService: NbAuthService,
    private nbTokenStorage: NbTokenStorage,
    private router: Router,
    private currentMemberProvider: CurrentMemberProvider,
    private urlHelper: UrlHelper,
  ) {
    this.oauth.configure(this.authCodeFlowConfig);
  }

  /**
   * Ask to the decath responsible OAuth Module to authenticate an internal user (PKCE OAuth)
   */
  public decathConnection(): Promise<boolean> {
    return this.oauth.loadDiscoveryDocumentAndLogin();
  }

  /**
   * Ask to the NbAuthModule to authenticate an external user thanks to Exchange strategy (Implicit OAuth)
   */
  public submitExternalCredentials(credentials: { email: string; password: string }): Observable<NbAuthResult> {
    return this.nbAuthService.authenticate(StrategyNames.extern, credentials);
  }

  /**
   * Logout from all authentication system
   */
  public logout() {
    if (!this.currentMemberProvider.isInternal()) {
      this.nbTokenStorage.clear();
      this.router.navigateByUrl(AppAuthService.loginPage);
    }

    this.oauth.logOut();
  }

  /**
   * Performs the necessary redirects according to the state of authentication
   *
   * @return
   */
  public authenticationRedirectFlow(): Observable<boolean> {
    this.oauth.setupAutomaticSilentRefresh();
    const discoverInternLoginState = from(this.oauth.loadDiscoveryDocumentAndTryLogin());

    return discoverInternLoginState.pipe(
      mergeMap(() => this.nbAuthService.onTokenChange()),
      map(() => this.currentMemberProvider.isAuthenticated()),
      tap(isAuthenticated => {
        if (isAuthenticated) {
          this.authenticatedRedirect();
        } else {
          this.unknownRedirect();
        }
      }),
    );
  }

  /**
   * Redirects the authenticated user according the first requested url
   */
  private authenticatedRedirect(): void {
    const entryRequest = localStorage.getItem('entryRequest');

    if (entryRequest) {
      this.router.navigateByUrl(entryRequest);
      localStorage.removeItem('entryRequest');
    } else if (window.location.pathname) {
      this.router.navigateByUrl(this.urlHelper.getPathNameWithoutBaseHref());
    } else {
      this.router.navigateByUrl(AppAuthService.defaultPage);
    }
  }

  /**
   * Redirects an unsafe user on the connection page
   */
  private unknownRedirect(): void {
    const pathWithoutBaseHref = this.urlHelper.getPathNameWithoutBaseHref();
    localStorage.setItem('entryRequest', pathWithoutBaseHref);

    this.router.navigateByUrl(pathWithoutBaseHref);
  }
}
